import React, { useState } from "react";
import "./styles/Whistleblowing.css";
import Footer from "../components/Footer";
import Wrapper from "../components/Wrapper";
import { useLanguage } from "../LanguageContext";
import { axiosInstance } from "../helper/axiosInstance";
import { whistleblowingText } from "./whistleblowingText";

const MAX_DESCRIPTION_LENGTH = 5000;
const MAX_ATTACHMENTS = 5;
const MAX_TOTAL_ATTACHMENT_BYTES = 20 * 1024 * 1024;

const EMPTY_FORM = {
    category: "",
    category_other: "",
    subject: "",
    description: "",
    incident_date: "",
    ongoing: "",
    location: "",
    persons_involved: "",
    reported_before: "",
    reported_before_details: "",
    witnesses: "",
    witnesses_details: "",
    relationship: "",
    anonymous: "",
    reporter_name: "",
    reporter_email: "",
    reporter_phone: "",
    consent: false
};

function Whistleblowing() {
    const { language } = useLanguage();
    const text = whistleblowingText[language] || whistleblowingText.ENG;

    const [form, setForm] = useState(EMPTY_FORM);
    const [files, setFiles] = useState([]);
    const [errors, setErrors] = useState({});
    const [submitting, setSubmitting] = useState(false);
    const [submitted, setSubmitted] = useState(false);
    const [submitError, setSubmitError] = useState("");

    const dynamicStyles = {
        fontFamily: language === "ARM" && "Mardoto",
        fontWeight: language === "ARM" && 500,
    };

    const isAnonymous = form.anonymous === "yes";

    const setField = (name, value) => {
        setForm((prev) => ({ ...prev, [name]: value }));
        setErrors((prev) => {
            if (!prev[name]) return prev;
            const next = { ...prev };
            delete next[name];
            return next;
        });
    };

    /*
     * Mirrors validate() in backend/routes/whistleblowing.js. A field the form
     * never showed is never required, so the conditional branches have to match
     * on both sides.
     */
    const validate = () => {
        const found = {};
        const v = text.validation;

        if (!form.category) found.category = v.category;
        if (form.category === "other" && !form.category_other.trim()) found.category_other = v.categoryOther;
        if (!form.subject.trim()) found.subject = v.subject;
        if (!form.description.trim()) found.description = v.description;
        // maxLength stops typing and pasting, but not a value set some other
        // way - and the server rejects over-length, so check it here too.
        else if (form.description.trim().length > MAX_DESCRIPTION_LENGTH) found.description = v.descriptionTooLong;
        if (!form.relationship) found.relationship = v.relationship;

        if (!form.reported_before) found.reported_before = v.reportedBefore;
        if (form.reported_before === "yes" && !form.reported_before_details.trim()) {
            found.reported_before_details = v.reportedBeforeDetails;
        }

        if (!form.witnesses) found.witnesses = v.witnesses;
        if (form.witnesses === "yes" && !form.witnesses_details.trim()) {
            found.witnesses_details = v.witnessesDetails;
        }

        if (!form.anonymous) found.anonymous = v.anonymous;
        if (form.anonymous === "no") {
            if (!form.reporter_name.trim()) found.reporter_name = v.name;
            if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.reporter_email.trim())) found.reporter_email = v.email;
        }

        if (!form.consent) found.consent = v.consent;

        if (files.length > MAX_ATTACHMENTS) found.attachments = v.attachmentsCount;
        else if (files.some(file => !/\.(pdf|jpe?g|png)$/i.test(file.name) || file.size === 0 || file.size > 10 * 1024 * 1024)) {
            found.attachments = v.attachmentsType;
        }
        else if (files.reduce((sum, file) => sum + file.size, 0) > MAX_TOTAL_ATTACHMENT_BYTES) {
            found.attachments = v.attachmentsSize;
        }

        return found;
    };

    /*
     * Read the selection before clearing the input, not inside the state
     * updater. React runs an updater lazily, and clearing the input empties
     * event.target.files first - so everything after the very first pick was
     * added as nothing. (The first one worked only because React evaluates the
     * first update in an empty queue eagerly.)
     */
    const handleSelectFiles = (event) => {
        const picked = Array.from(event.target.files);
        // Lets the same file be chosen again after it was removed.
        event.target.value = "";

        setFiles((prev) => {
            const identity = (file) => `${file.name}:${file.size}:${file.lastModified}`;
            const already = new Set(prev.map(identity));
            return [...prev, ...picked.filter((file) => !already.has(identity(file)))];
        });

        setErrors((prev) => {
            const next = { ...prev };
            delete next.attachments;
            return next;
        });
    };

    const handleRemoveFile = (indexToRemove) => {
        setFiles((prev) => prev.filter((_, index) => index !== indexToRemove));
    };

    const handleSubmit = async (event) => {
        event.preventDefault();
        setSubmitError("");

        const found = validate();
        setErrors(found);
        if (Object.keys(found).length > 0) {
            document.querySelector(".whistleblowing-error")?.scrollIntoView({ behavior: "smooth", block: "center" });
            return;
        }

        const payload = new FormData();
        Object.entries(form).forEach(([name, value]) => {
            // Anonymous reports must not carry identifying fields even if they
            // were typed before the reporter switched the answer to "yes".
            if (isAnonymous && ["reporter_name", "reporter_email", "reporter_phone"].includes(name)) return;
            payload.append(name, typeof value === "boolean" ? String(value) : value);
        });
        files.forEach((file) => payload.append("attachments", file));

        setSubmitting(true);
        try {
            await axiosInstance.post("/whistleblowing", payload, {
                headers: { "Content-Type": "multipart/form-data" }
            });
            setSubmitted(true);
            setForm(EMPTY_FORM);
            setFiles([]);
            window.scrollTo({ top: 0, behavior: "smooth" });
        } catch (error) {
            const responseErrors = error.response?.data?.errors;
            setSubmitError(responseErrors?.length ? responseErrors.join(" ") : text.validation.network);
        } finally {
            setSubmitting(false);
        }
    };

    const renderError = (name) => errors[name] && (
        <span className="whistleblowing-error" style={dynamicStyles}>{errors[name]}</span>
    );

    const renderYesNo = (name, label, note) => (
        <div className="whistleblowing-field">
            <label className="whistleblowing-label" style={dynamicStyles}>
                {label} <span className="whistleblowing-required">*</span>
            </label>
            {note && <p className="whistleblowing-note" style={dynamicStyles}>{note}</p>}
            <div className="whistleblowing-radios">
                {["yes", "no"].map((option) => (
                    <label key={option} className="whistleblowing-radio" style={dynamicStyles}>
                        <input
                            type="radio"
                            name={name}
                            value={option}
                            checked={form[name] === option}
                            onChange={(e) => setField(name, e.target.value)}
                        />
                        {option === "yes" ? text.yes : text.no}
                    </label>
                ))}
            </div>
            {renderError(name)}
        </div>
    );

    return (
        <Wrapper>
            <div className="whistleblowing">
                <div className="whistleblowing-header">
                    <span style={dynamicStyles}>{text.header}</span>
                    <div className="whistleblowing-header_icons">
                        <svg width="12" height="12" viewBox="0 0 12 12" fill="none" xmlns="http://www.w3.org/2000/svg">
                            <path
                                d="M0.0164407 3.82196L0 12L3.99046 12L3.99046 3.99715C4.00629 3.99715 4.9794 4.00325 4.9794 4.0002L12 4.0002L12 -5.24537e-07L3.83761 -1.67747e-07C1.73064 -7.56487e-08 0.0219223 1.70984 0.0176598 3.82196"
                                fill="#243BE2"
                            />
                        </svg>
                        <svg width="12" height="12" viewBox="0 0 12 12" fill="none" xmlns="http://www.w3.org/2000/svg">
                            <path
                                d="M8.17804 11.9836L0 12L0 8.00954L8.00285 8.00954C8.00285 7.99371 7.99675 7.0206 7.9998 7.0206L7.9998 0H12L12 8.16239C12 10.2694 10.2902 11.9781 8.17804 11.9823"
                                fill="#ADF233"
                            />
                        </svg>
                    </div>
                </div>

                <div className="whistleblowing-title">
                    <h1 style={dynamicStyles}>{text.title}</h1>
                </div>

                {submitted ? (
                    <div className="whistleblowing-success">
                        <h2 style={dynamicStyles}>{text.successTitle}</h2>
                        <p style={dynamicStyles}>{text.successBody}</p>
                        <button type="button" className="whistleblowing-submit" style={dynamicStyles} onClick={() => setSubmitted(false)}>
                            {text.successAgain}
                        </button>
                    </div>
                ) : (
                    <>
                        <div className="whistleblowing-description">
                            <p style={dynamicStyles}>{text.intro}</p>
                        </div>

                        <form className="whistleblowing-form" onSubmit={handleSubmit} noValidate>
                            <div className="whistleblowing-field">
                                <label htmlFor="category" className="whistleblowing-label" style={dynamicStyles}>
                                    {text.categoryLabel} <span className="whistleblowing-required">*</span>
                                </label>
                                <select
                                    id="category"
                                    className="whistleblowing-input"
                                    style={dynamicStyles}
                                    value={form.category}
                                    onChange={(e) => setField("category", e.target.value)}
                                >
                                    <option value="">{text.categoryPlaceholder}</option>
                                    {Object.entries(text.categories).map(([value, label]) => (
                                        <option key={value} value={value}>{label}</option>
                                    ))}
                                </select>
                                {renderError("category")}
                            </div>

                            {form.category === "other" && (
                                <div className="whistleblowing-field">
                                    <label htmlFor="category_other" className="whistleblowing-label" style={dynamicStyles}>
                                        {text.categoryOtherLabel} <span className="whistleblowing-required">*</span>
                                    </label>
                                    <input
                                        id="category_other"
                                        type="text"
                                        className="whistleblowing-input"
                                        style={dynamicStyles}
                                        value={form.category_other}
                                        onChange={(e) => setField("category_other", e.target.value)}
                                    />
                                    {renderError("category_other")}
                                </div>
                            )}

                            <div className="whistleblowing-field">
                                <label htmlFor="subject" className="whistleblowing-label" style={dynamicStyles}>
                                    {text.subjectLabel} <span className="whistleblowing-required">*</span>
                                </label>
                                <input
                                    id="subject"
                                    type="text"
                                    className="whistleblowing-input"
                                    style={dynamicStyles}
                                    placeholder={text.subjectPlaceholder}
                                    value={form.subject}
                                    onChange={(e) => setField("subject", e.target.value)}
                                />
                                {renderError("subject")}
                            </div>

                            <div className="whistleblowing-field">
                                <label htmlFor="description" className="whistleblowing-label" style={dynamicStyles}>
                                    {text.descriptionLabel} <span className="whistleblowing-required">*</span>
                                </label>
                                <textarea
                                    id="description"
                                    rows="8"
                                    maxLength={MAX_DESCRIPTION_LENGTH}
                                    className="whistleblowing-input"
                                    style={dynamicStyles}
                                    placeholder={text.descriptionPlaceholder}
                                    value={form.description}
                                    onChange={(e) => setField("description", e.target.value)}
                                />
                                <span className="whistleblowing-hint" style={dynamicStyles}>{text.descriptionHint}</span>
                                {renderError("description")}
                            </div>

                            <div className="whistleblowing-row">
                                <div className="whistleblowing-field">
                                    <label htmlFor="incident_date" className="whistleblowing-label" style={dynamicStyles}>
                                        {text.incidentDateLabel}
                                    </label>
                                    <input
                                        id="incident_date"
                                        type="date"
                                        className="whistleblowing-input"
                                        style={dynamicStyles}
                                        value={form.incident_date}
                                        onChange={(e) => setField("incident_date", e.target.value)}
                                    />
                                </div>
                                <div className="whistleblowing-field">
                                    <label htmlFor="location" className="whistleblowing-label" style={dynamicStyles}>
                                        {text.locationLabel}
                                    </label>
                                    <input
                                        id="location"
                                        type="text"
                                        className="whistleblowing-input"
                                        style={dynamicStyles}
                                        placeholder={text.locationPlaceholder}
                                        value={form.location}
                                        onChange={(e) => setField("location", e.target.value)}
                                    />
                                </div>
                            </div>

                            <div className="whistleblowing-field">
                                <label className="whistleblowing-label" style={dynamicStyles}>{text.ongoingLabel}</label>
                                <div className="whistleblowing-radios">
                                    {["yes", "no"].map((option) => (
                                        <label key={option} className="whistleblowing-radio" style={dynamicStyles}>
                                            <input
                                                type="radio"
                                                name="ongoing"
                                                value={option}
                                                checked={form.ongoing === option}
                                                onChange={(e) => setField("ongoing", e.target.value)}
                                            />
                                            {option === "yes" ? text.yes : text.no}
                                        </label>
                                    ))}
                                </div>
                            </div>

                            <div className="whistleblowing-field">
                                <label htmlFor="persons_involved" className="whistleblowing-label" style={dynamicStyles}>
                                    {text.personsInvolvedLabel}
                                </label>
                                <textarea
                                    id="persons_involved"
                                    rows="3"
                                    className="whistleblowing-input"
                                    style={dynamicStyles}
                                    placeholder={text.personsInvolvedPlaceholder}
                                    value={form.persons_involved}
                                    onChange={(e) => setField("persons_involved", e.target.value)}
                                />
                            </div>

                            {renderYesNo("reported_before", text.reportedBeforeLabel)}
                            {form.reported_before === "yes" && (
                                <div className="whistleblowing-field">
                                    <label htmlFor="reported_before_details" className="whistleblowing-label" style={dynamicStyles}>
                                        {text.reportedBeforeDetailsLabel} <span className="whistleblowing-required">*</span>
                                    </label>
                                    <textarea
                                        id="reported_before_details"
                                        rows="3"
                                        className="whistleblowing-input"
                                        style={dynamicStyles}
                                        value={form.reported_before_details}
                                        onChange={(e) => setField("reported_before_details", e.target.value)}
                                    />
                                    {renderError("reported_before_details")}
                                </div>
                            )}

                            {renderYesNo("witnesses", text.witnessesLabel)}
                            {form.witnesses === "yes" && (
                                <div className="whistleblowing-field">
                                    <label htmlFor="witnesses_details" className="whistleblowing-label" style={dynamicStyles}>
                                        {text.witnessesDetailsLabel} <span className="whistleblowing-required">*</span>
                                    </label>
                                    <textarea
                                        id="witnesses_details"
                                        rows="3"
                                        className="whistleblowing-input"
                                        style={dynamicStyles}
                                        value={form.witnesses_details}
                                        onChange={(e) => setField("witnesses_details", e.target.value)}
                                    />
                                    {renderError("witnesses_details")}
                                </div>
                            )}

                            <div className="whistleblowing-field">
                                <label htmlFor="relationship" className="whistleblowing-label" style={dynamicStyles}>
                                    {text.relationshipLabel} <span className="whistleblowing-required">*</span>
                                </label>
                                <select
                                    id="relationship"
                                    className="whistleblowing-input"
                                    style={dynamicStyles}
                                    value={form.relationship}
                                    onChange={(e) => setField("relationship", e.target.value)}
                                >
                                    <option value="">{text.relationshipPlaceholder}</option>
                                    {Object.entries(text.relationships).map(([value, label]) => (
                                        <option key={value} value={value}>{label}</option>
                                    ))}
                                </select>
                                {renderError("relationship")}
                            </div>

                            {renderYesNo("anonymous", text.anonymousLabel)}

                            {form.anonymous === "no" && (
                                <div className="whistleblowing-row">
                                    <div className="whistleblowing-field">
                                        <label htmlFor="reporter_name" className="whistleblowing-label" style={dynamicStyles}>
                                            {text.nameLabel} <span className="whistleblowing-required">*</span>
                                        </label>
                                        <input
                                            id="reporter_name"
                                            type="text"
                                            className="whistleblowing-input"
                                            style={dynamicStyles}
                                            value={form.reporter_name}
                                            onChange={(e) => setField("reporter_name", e.target.value)}
                                        />
                                        {renderError("reporter_name")}
                                    </div>
                                    <div className="whistleblowing-field">
                                        <label htmlFor="reporter_email" className="whistleblowing-label" style={dynamicStyles}>
                                            {text.emailLabel} <span className="whistleblowing-required">*</span>
                                        </label>
                                        <input
                                            id="reporter_email"
                                            type="email"
                                            className="whistleblowing-input"
                                            style={dynamicStyles}
                                            value={form.reporter_email}
                                            onChange={(e) => setField("reporter_email", e.target.value)}
                                        />
                                        {renderError("reporter_email")}
                                    </div>
                                    <div className="whistleblowing-field">
                                        <label htmlFor="reporter_phone" className="whistleblowing-label" style={dynamicStyles}>
                                            {text.phoneLabel}
                                        </label>
                                        <input
                                            id="reporter_phone"
                                            type="tel"
                                            className="whistleblowing-input"
                                            style={dynamicStyles}
                                            value={form.reporter_phone}
                                            onChange={(e) => setField("reporter_phone", e.target.value)}
                                        />
                                    </div>
                                </div>
                            )}

                            <div className="whistleblowing-field">
                                <label htmlFor="attachments" className="whistleblowing-label" style={dynamicStyles}>
                                    {text.attachmentsLabel}
                                </label>
                                <input
                                    id="attachments"
                                    type="file"
                                    multiple
                                    className="whistleblowing-file-input"
                                    accept=".pdf,.jpg,.jpeg,.png"
                                    onChange={handleSelectFiles}
                                />
                                <label htmlFor="attachments" className="whistleblowing-file-button" style={dynamicStyles}>
                                    {text.attachmentsChoose}
                                </label>
                                <span className="whistleblowing-hint" style={dynamicStyles}>{text.attachmentsHint}</span>
                                {files.length > 0 && (
                                    <ul className="whistleblowing-files">
                                        {files.map((file, index) => (
                                            <li key={`${file.name}-${index}`} style={dynamicStyles}>
                                                <span>{file.name}</span>
                                                <button type="button" onClick={() => handleRemoveFile(index)}>
                                                    {text.attachmentsRemove}
                                                </button>
                                            </li>
                                        ))}
                                    </ul>
                                )}
                                {renderError("attachments")}
                            </div>

                            <div className="whistleblowing-field">
                                <label className="whistleblowing-consent" style={dynamicStyles}>
                                    <input
                                        type="checkbox"
                                        checked={form.consent}
                                        onChange={(e) => setField("consent", e.target.checked)}
                                    />
                                    {text.consentLabel} <span className="whistleblowing-required">*</span>
                                </label>
                                {renderError("consent")}
                            </div>

                            {submitError && (
                                <div className="whistleblowing-submit-error" style={dynamicStyles}>
                                    <strong>{text.errorTitle}</strong>
                                    <p>{submitError}</p>
                                </div>
                            )}

                            <button type="submit" className="whistleblowing-submit" style={dynamicStyles} disabled={submitting}>
                                {submitting ? text.submitting : text.submit}
                            </button>
                        </form>
                    </>
                )}
            </div>
            <Footer />
        </Wrapper>
    );
}

export default Whistleblowing;
